import { useEffect, useState } from 'react';
import '../App.sass';
import { useModalContext } from '../components/ModalProvider';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { useAdministrationAPI } from '../AdministrationAPI';
import { userAPI } from '../UserAPI';
import dayjs from 'dayjs';
import { Invoice, InvoiceStatus, Order } from '@the-real-insight/tri-model';
import Section from '../components/Section';
import LoadingIndicator from '../components/LoadingIndicator';
import DataTable from 'react-data-table-component';
import { ActionButton, Pivot, PivotItem, Text, TooltipHost } from '@fluentui/react';
import { dataTableStyles } from '../styles';
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';
import { useFormats } from '../hooks/formats';
import { downloadFile, downloadInvoice, PDFInvoice } from '../billing/invoice';
import StripePaymentMethod from '../components/StripePaymentMethod';
import MarketplacePaymentSummary from '../components/MarketplacePaymentSummary';
import PaymentAPITest from '../components/PaymentAPITest';
import StripeAccountForm from '../components/StripeAccountForm';

interface Properties {
}

export default function BillingPanel(properties: Properties) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError } = useModalContext() as any;
    const { currencyFormat } = useFormats();
    const { getInvoiceForecast, getInvoices } = useAdministrationAPI();
    const [customer, setCustomer] = useState() as any;
    const [invoices, setInvoices] = useState([]) as any;
    const [currentBillingPeriodFrom, setCurrentBillingPeriodFrom] = useState(dayjs().startOf('M').toDate()) as any;
    const [currentBillingPeriodTo, setCurrentBillingPeriodTo] = useState(dayjs().endOf('M').toDate()) as any;
    const [currentInvoices, setCurrentInvoices] = useState() as any;
    const [currentInvoicePDF, setCurrentInvoicePDF] = useState() as any;
    const [tab, setTab] = useState('creditors');
    const [ordersAsBuyer, setOrdersAsBuyer] = useState([]) as any;
    const [ordersAsSeller, setOrdersAsSeller] = useState([]) as any;

    // Debug tab changes
    useEffect(() => {
        console.log('Tab changed to:', tab);
    }, [tab]);

    const setParams = (item?: PivotItem) => {
        console.log('Pivot clicked:', item);
        if (item && item.props && item.props.itemKey) {
            const newTab = item.props.itemKey;
            console.log('Setting tab to:', newTab);
            setTab(newTab);
        }
    }

    useEffect(() => {
        const call = async () => {
            const newCustomer = await userAPI.getCustomer(authentication.customer);

            newCustomer.address = newCustomer.address || {};

            setCustomer(newCustomer);

            const ordersAsBuyer = await userAPI.getOrdersAsBuyer(authentication.customer, dayjs().startOf('M').toDate(), dayjs().endOf('M').toDate());
            const ordersAsSeller = await userAPI.getOrdersAsSeller(authentication.customer, dayjs().startOf('M').toDate(), dayjs().endOf('M').toDate());

            console.log('Orders as buyer >>>', ordersAsBuyer);
            console.log('Orders as seller >>>', ordersAsSeller);

            setOrdersAsBuyer(ordersAsBuyer);
            setOrdersAsSeller(ordersAsSeller);

            const invoiceForecast = await getInvoiceForecast(authentication.customer,
                dayjs().startOf('M').toDate(), dayjs().endOf('M').toDate());

            console.log('Invoice forecast >>>', invoiceForecast);

            setCurrentInvoices(invoiceForecast);

            // if (customer && invoiceForecast) {
            //     setCurrentInvoicePDF(<PDFInvoice invoice={invoiceForecast} customer={customer}></PDFInvoice>);
            // }

            const newInvoices = (await getInvoices(authentication.customer, new Date('2025-06-01T00:00:00'), new Date()))
                .sort((a: Invoice, b: Invoice) => b.periodFromDate.getTime() - a.periodFromDate.getTime());

            console.log('Invoices >>>', newInvoices);

            setInvoices(newInvoices);
        };

        try {
            call();
        } catch (error: any) {
            pushError(error);
        }
    }, []);

    const forecastColumns = [{
        name: 'Anbieter',
        width: '200px',
        cell: (invoice: Invoice) => (
            <div>
                {invoice.seller ? <Text variant="medium">{invoice.seller.name}, {invoice.seller.headquarters?.city}</Text> : <Text variant="medium">Internal</Text>}
            </div>
        ),
    }, {
        name: t('billingPanel.amountColumn'),
        width: '100px',
        right: true,
        cell: (invoice: Invoice) => (
            <Text variant="medium">{currencyFormat.format(invoice.subtotal())}</Text>
        ),
    }, {
        name: '',
        width: '100px',
        cell: (invoice: any) => (
            <div className='displayFlex'>
                <TooltipHost content="Download invoice forecast PDF">
                    <ActionButton iconProps={{ iconName: 'FileCode' }} onClick={() => downloadInvoice(invoice)}>
                    </ActionButton>
                </TooltipHost>
                <TooltipHost content="Download invoice forecast as E-Rechnung">
                    <ActionButton iconProps={{ iconName: 'PDF' }} onClick={async () => {
                        const blob = await pdf(<PDFInvoice invoice={invoice} customer={customer}></PDFInvoice>).toBlob();

                        console.log(blob);

                        downloadFile(blob, `Rechnung ${invoice.id}`);
                    }}>
                    </ActionButton>
                </TooltipHost>
            </div>
        ),
    },
    ];

    const columns = [{
        name: t('billingPanel.paymentStatusColumn'),
        width: '100px',
        cell: (invoice: Invoice) => (
            <div className="width50 paddingS displayFlex">
                <div className={(invoice.status === InvoiceStatus.paid ? 'colorGreen' : 'colorRed') + ' fontWeightBold textS'}>{invoice.status === InvoiceStatus.paid ? 'Bezahlt' : 'Offen'}</div>
            </div>
        ),
    }, {
        name: 'Anbieter',
        width: '200px',
        cell: (invoice: Invoice) => (
            <div>
                {invoice.seller ? <div className="textS">{invoice.seller.name}, {invoice.seller.headquarters?.city}</div> : <div className="textS">Internal</div>}
            </div>
        ),
    }, {
        name: t('billingPanel.invoiceDateColumn'),
        width: '120px',
        right: true,
        cell: (invoice: Invoice) => (
            <div className="textS">{dayjs(invoice.creationDate).format(t('global.dateShort'))}</div>
        ),
    }, {
        name: t('billingPanel.invoicePeriodColumn'),
        width: '200px',
        cell: (invoice: Invoice) => (
            <div className="textS">{dayjs(invoice.periodFromDate).format(t('global.dateShort'))} - {dayjs(new Date(invoice.periodToDate)).format(t('global.dateShort'))}</div>
        ),
    }, {
        name: t('billingPanel.amountColumn'),
        width: '120px',
        right: true,
        cell: (invoice: Invoice) => (
            <div className="textS">{currencyFormat.format(invoice.subtotal())}</div>
        ),
    }, {
        name: '',
        width: '100px',
        cell: (invoice: any) => (
            <div className='displayFlex'>
                <TooltipHost content="Download invoice forecast PDF">
                    <ActionButton iconProps={{ iconName: 'FileCode' }} onClick={() => downloadInvoice(invoice)}>
                    </ActionButton>
                </TooltipHost>
                <TooltipHost content="Download invoice forecast as E-Rechnung">
                    <ActionButton iconProps={{ iconName: 'PDF' }} onClick={async () => {
                        const blob = await pdf(<PDFInvoice invoice={invoice} customer={customer}></PDFInvoice>).toBlob();

                        console.log(blob);

                        downloadFile(blob, `Rechnung ${invoice.id}`);
                    }}>
                    </ActionButton>
                </TooltipHost>
            </div>
        ),
    },
    ];

    const orderColumn = [{
        name: 'ID',
        width: '100px',
        cell: (order: Order) => (
            <div className="width50 paddingS displayFlex">
                <Text variant="medium">{order._id}</Text>
            </div>
        ),
    }, {
        name: 'Anbieter',
        width: '200px',
        cell: (order: Order) => (
            <div className="width50 paddingS displayFlex">
                <Text variant="medium">{order.seller}</Text>
            </div>
        ),
    }, {
        name: 'Dienste',
        width: '200px',
        cell: (order: Order) => (
            <div className="width50 paddingS displayFlex">
            </div>
        ),
    }];

    return (
        <div>
            <Pivot onLinkClick={(item) => {
                console.log('Pivot onLinkClick:', item);
                if (item && item.props && item.props.itemKey) {
                    setTab(item.props.itemKey);
                }
            }} selectedKey={tab} overflowBehavior={'menu'}>
                <PivotItem itemKey="creditors" headerText={'Kreditoren'} itemIcon="PaymentCard">
                    <div className='marginTopM displayFlex gapM'>
                        <div className='flexGrow1'>
                            <Section title={`Aktueller Abrechnungszeitraum ${dayjs(currentBillingPeriodFrom).format(t('global.dateShort'))} - ${dayjs(currentBillingPeriodTo).format(t('global.dateShort'))}`}>
                                <DataTable
                                    columns={forecastColumns}
                                    data={currentInvoices}
                                    customStyles={dataTableStyles}
                                    pagination
                                    noDataComponent={<LoadingIndicator loading={false} text={t('settingsPanel.projectsPanel.noObjectsDefined')}></LoadingIndicator>}
                                />
                            </Section>
                            <Section className='marginTopM' title={'Bisherige Bestellungen'}>
                                <div className='displayFlex justifyContentEnd'>
                                    <ActionButton onClick={async () => {
                                        console.log('Creating missing invoices');
                                        //await userAPI.createMissingInvoicesForProject(authentication.customer, properties.project._id);
                                    }}>
                                        {t('billingPanel.createInvoice')}
                                    </ActionButton>
                                </div>
                                <DataTable
                                    columns={columns}
                                    data={invoices}
                                    customStyles={dataTableStyles}
                                    pagination
                                    noDataComponent={<LoadingIndicator loading={false} text={t('settingsPanel.projectsPanel.noObjectsDefined')}></LoadingIndicator>}
                                />
                            </Section>
                        </div>
                        <div className='flexGrow1'>
                            <Section title={'Eigene Bestellungen'}>
                                <DataTable
                                    columns={orderColumn}
                                    data={ordersAsBuyer}
                                    customStyles={dataTableStyles}
                                    selectableRows
                                />
                            </Section>
                        </div>
                    </div>
                </PivotItem>
                <PivotItem itemKey="debtors" headerText={'Debitoren'} itemIcon="Money">
                    <div>Kreditoren</div>
                </PivotItem>
                <PivotItem itemKey="payments" headerText={'Zahlungsdaten'} itemIcon="Money">
                    <div className='marginTopM displayFlex gapM'>
                        <div style={{ width: 'calc(50% - 8px)' }}>
                            <Section title={'Zahlungsdaten'}>
                                <StripePaymentMethod
                                    customerId={authentication.customer}
                                    onPaymentMethodAdded={(paymentMethod: any) => {
                                        console.log('Payment method added:', paymentMethod);
                                    }}
                                    onPaymentMethodRemoved={(paymentMethodId: string) => {
                                        console.log('Payment method removed:', paymentMethodId);
                                    }}
                                />
                            </Section>
                        </div>
                        <div style={{ width: 'calc(50% - 8px)' }}>
                            <Section title={'Stripe-Konto für Zahlungseingang'}>
                                <StripeAccountForm
                                    customerId={authentication.customer}
                                    onSuccess={(accountData: any) => {
                                        console.log('Stripe account created:', accountData);
                                    }}
                                    onCancel={() => {
                                        console.log('Stripe account creation cancelled');
                                    }}
                                />
                            </Section>
                        </div>
                    </div>
                    <Section className='marginTopM' title={'Marktplatz Zahlungsübersicht'}>
                        <MarketplacePaymentSummary
                            customerId={authentication.customer}
                        />
                    </Section>
                    {/* <Section className='marginTopM' title={'API Connection Test'}>
                        <PaymentAPITest
                            customerId={authentication.customer}
                        />
                    </Section> */}
                </PivotItem>
            </Pivot>
        </div>
    );
}